import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { ThemeProvider } from "@/theme/ThemeContext";
import { Header } from "@/components/onix/Header";
import { FooterInstitutional } from "@/components/onix/FooterInstitutional";
import { CookieConsent } from "@/components/onix/CookieConsent";
import Home from "@/pages/Home";
import Ecosistema from "@/pages/Ecosistema";
import Herramienta from "@/pages/Herramienta";
import Soluciones from "@/pages/Soluciones";
import Seguridad from "@/pages/Seguridad";
import Empresa from "@/pages/Empresa";
import Contacto from "@/pages/Contacto";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminPanel from "@/pages/admin/AdminPanel";
import { Tracker } from "@/components/onix/Tracker";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const SmoothScroll = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, []);
  return null;
};

const SkipLink = () => {
  const { t } = useLang();
  return (
    <a
      href="#contenido"
      className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-onix-white focus:text-onix-black focus:px-4 focus:py-2 text-xs font-bold uppercase tracking-widest"
    >
      {t.skip}
    </a>
  );
};

const Shell = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <>
      <ScrollToTop />
      {!isAdmin && <SmoothScroll />}
      {!isAdmin && <SkipLink />}
      {!isAdmin && <Header />}
      <Tracker />
      <div id="contenido">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/ecosistema" element={<Ecosistema />} />
            <Route path="/herramientas" element={<Navigate to="/ecosistema" replace />} />
            <Route path="/herramientas/:slug" element={<Herramienta />} />
            <Route path="/soluciones" element={<Soluciones />} />
            <Route path="/seguridad-y-gobierno" element={<Seguridad />} />
            <Route path="/empresa" element={<Empresa />} />
            <Route path="/contacto" element={<Contacto />} />
            <Route path="/aviso-legal" element={<LegalPage slug="aviso-legal" />} />
            <Route path="/privacidad" element={<LegalPage slug="privacidad" />} />
            <Route path="/cookies" element={<LegalPage slug="cookies" />} />
            <Route path="/terminos-de-uso" element={<LegalPage slug="terminos-de-uso" />} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/panel" element={<AdminPanel />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
      </div>
      {!isAdmin && <FooterInstitutional />}
      {!isAdmin && <CookieConsent />}
    </>
  );
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
